import { z } from 'zod';
import { GROUP_TYPES, LEVEL_LABEL, LEVEL_PARENT, USER_LEVELS, US_STATE_CODES } from '../enums';
import type { GroupType, RecordStatus, UserLevel } from '../enums';
import { listQuerySchema, objectId, optionalText, queryBool, recordStatus } from './common';

/* --------------------------------- Groups --------------------------------- */

export const groupInputSchema = z.object({
  name: z.string().trim().min(2, 'Group name is required').max(80),
  status: recordStatus.default('Active'),
  permissionCodes: z.array(z.string().regex(/^PC\d+$/)).default([]),
  /** Cents. The reference exposes both limits per group; their enforcement is not built yet. */
  discountCreditLimitCents: z.number().int().min(0).nullable().optional(),
  walletLimitCents: z.number().int().min(0).nullable().optional(),
});
export type GroupInput = z.input<typeof groupInputSchema>;
export const groupUpdateSchema = groupInputSchema.partial();
export type GroupUpdate = z.input<typeof groupUpdateSchema>;

export const groupCopySchema = z.object({ name: z.string().trim().min(2).max(80) });

/** "Replace Group Permissions": the group whose permissions this group takes ("Select source group"). */
export const groupReplaceSchema = z.object({
  sourceGroupId: z.string().regex(/^[a-f\d]{24}$/i, 'Select source group'),
});
export type GroupReplaceInput = z.input<typeof groupReplaceSchema>;

/**
 * "Assign Group" (the owner's reading, 2026-10-24): the groups the members of this group may
 * give to the users they add or edit. A group without a list may give every group.
 * Assumption: at least one group must be ticked.
 */
export const groupAssignSchema = z.object({
  groupIds: z
    .array(z.string().regex(/^[a-f\d]{24}$/i, 'Unknown group'))
    .min(1, 'Select at least one group')
    .max(1000),
});
export type GroupAssignInput = z.input<typeof groupAssignSchema>;

/** A group in the selects of the user forms; `assignable` is false for one the caller may not give. */
export interface GroupOptionDto {
  id: string;
  name: string;
  assignable: boolean;
}

export const groupListQuerySchema = listQuerySchema.extend({
  q: optionalText(),
  status: recordStatus.optional(),
  type: z.enum(GROUP_TYPES).optional(),
});
export type GroupListQuery = z.input<typeof groupListQuerySchema>;

export interface GroupDto {
  id: string;
  name: string;
  type: GroupType;
  status: RecordStatus;
  permissionCodes: string[];
  discountCreditLimitCents: number | null;
  walletLimitCents: number | null;
  /** "Assign Group": the groups its members may give; null means every group. */
  assignableGroupIds: string[] | null;
  /** Member counts per level, as shown on the reference group list. */
  members: {
    employees: number;
    retailers: number;
    distributors: number;
    masters: number;
    admins: number;
  };
  createdAt: string;
  updatedAt: string;
}

/* ---------------------------------- Users --------------------------------- */

/**
 * The add forms of the four levels follow docs/product-inventory.md, appendix 11.6: sections
 * Assignment Details, Personal Details, Address Details and Commission & Access, with the
 * fields, lengths and required marks read there.
 */

/** "Type" on the master agent form. */
export const MASTER_AGENT_TYPES = ['Master', 'Vendor', 'Shipper'] as const;
export type MasterAgentType = (typeof MASTER_AGENT_TYPES)[number];

/** The code beside "Matrix commission" on each level's form: needed to give that level a matrix. */
export const MATRIX_COMMISSION_PERMISSION: Record<UserLevel, string | null> = {
  admin: null,
  master: 'PC1250',
  distributor: 'PC1247',
  retailer: 'PC1248',
  employee: 'PC1249',
};

/**
 * Yes / No switches of "Commission & Access" and the levels whose form has them. They are
 * stored with the user; nothing reads them yet (no one-time-password service, picture
 * capture, agent locator or phone requests exist here).
 */
export const USER_ACCESS_FLAGS = [
  ['otp', 'OTP', ['master', 'distributor', 'retailer', 'employee']],
  ['takePicture', 'Take a picture options', ['master']],
  ['showOnAgentLocator', 'Show on agent locator', ['distributor']],
  ['allowMultiplePhoneRequest', 'Allow multiple phone request', ['distributor']],
  ['addNewPhoneRequest', 'Add new phone request', ['distributor']],
  ['shipperRole', 'Shipper Role', ['employee']],
] as const;
export type UserAccessFlag = (typeof USER_ACCESS_FLAGS)[number][0];
export const accessFlagsOf = (level: UserLevel) =>
  USER_ACCESS_FLAGS.filter(([, , levels]) => (levels as readonly string[]).includes(level));

const addressSchema = z.object({
  line1: z.string().trim().min(1, 'Address 1 is required').max(50),
  line2: z.string().trim().max(50).optional().default(''),
  zip: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'Zip must be 5 digits'),
  city: z.string().trim().min(1, 'City is required').max(60),
  // A refinement, not an enum: an enum failure stops the whole form from being checked, and
  // the rules that depend on the level (company name, parent) would then show one submit late.
  state: z
    .string()
    .trim()
    .refine((v) => US_STATE_CODES.includes(v), { message: 'State is required' }),
});

/** Minimum 8 characters with a letter and a digit. The reference's own policy was not observable. */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(200)
  .regex(/[A-Za-z]/, 'Password must contain a letter')
  .regex(/\d/, 'Password must contain a digit');

const accessFlagShape = Object.fromEntries(
  USER_ACCESS_FLAGS.map(([key]) => [key, z.boolean().optional()]),
) as Record<UserAccessFlag, z.ZodOptional<z.ZodBoolean>>;

const userBase = z.object({
  firstName: z.string().trim().min(1, 'First Name is required').max(20),
  lastName: z.string().trim().min(1, 'Last Name is required').max(20),
  /** "Agent company name": required for a master agent, not asked on the other forms. */
  companyName: z.string().trim().max(100).optional().default(''),
  /** Master agent only. */
  agentType: z.enum(MASTER_AGENT_TYPES).nullable().optional(),
  email: z
    .string()
    .trim()
    .min(1, 'Email Address is required')
    .max(50)
    .email('Enter a valid email address'),
  phone: z
    .string()
    .trim()
    .regex(/^(\d{10})?$/, 'Phone Number must be 10 digits')
    .optional()
    .default(''),
  address: addressSchema,
  groupId: z.string().regex(/^[a-f\d]{24}$/i, 'Select a user group'),
  /** "Matrix commission": null is the form's "No Commission". Needs the level's own code. */
  commissionMatrixId: objectId.nullable().optional(),
  ...accessFlagShape,
  status: recordStatus.default('Active'),
});

export const userCreateSchema = userBase
  .extend({
    loginId: z
      .string()
      .trim()
      .min(3, 'Login Id must be at least 3 characters')
      .max(40)
      .regex(/^[A-Za-z0-9._-]+$/, 'Login ID may contain letters, digits, dot, dash and underscore'),
    password: passwordSchema,
    level: z.enum(USER_LEVELS),
    /** Required for distributor, retailer and employee: the user one level up. */
    parentId: objectId.nullable().optional(),
  })
  .superRefine((v, ctx) => {
    const parentLevel = LEVEL_PARENT[v.level];
    if (parentLevel && !v.parentId)
      ctx.addIssue({
        code: 'custom',
        path: ['parentId'],
        message: `Select the ${LEVEL_LABEL[parentLevel].toLowerCase()}`,
      });
    if (v.level === 'master') {
      if (!v.companyName)
        ctx.addIssue({
          code: 'custom',
          path: ['companyName'],
          message: 'Agent company name is required',
        });
      if (!v.agentType)
        ctx.addIssue({ code: 'custom', path: ['agentType'], message: 'Select type' });
    }
  });
export type UserCreateInput = z.input<typeof userCreateSchema>;

export const userUpdateSchema = userBase.partial().extend({
  password: passwordSchema.optional(),
});
export type UserUpdateInput = z.input<typeof userUpdateSchema>;

export const userListQuerySchema = listQuerySchema.extend({
  level: z.enum(USER_LEVELS).optional(),
  q: optionalText(),
  status: recordStatus.optional(),
  parentId: objectId.optional(),
  locked: queryBool,
});
export type UserListQuery = z.input<typeof userListQuerySchema>;

export const userOptionsQuerySchema = z.object({
  level: z.enum(USER_LEVELS),
  parentId: objectId.optional(),
});

/** Auto-suggest of a login ID ("Who got it back (Employee Login ID)"): three characters at least. */
export const userSuggestQuerySchema = z.object({
  q: z.string().trim().min(3, 'Type at least 3 characters').max(40),
});

export const unlockSchema = z.object({
  loginId: z.string().trim().min(1, 'User name is required').max(40),
});

export interface UserRef {
  id: string;
  name: string;
  loginId: string;
}

export interface UserDto extends Record<UserAccessFlag, boolean> {
  id: string;
  loginId: string;
  /** First and last name together, as lists show it. */
  name: string;
  firstName: string;
  lastName: string;
  companyName: string;
  agentType: MasterAgentType | null;
  email: string;
  phone: string;
  address: { line1: string; line2: string; city: string; state: string; zip: string };
  commissionMatrix: { id: string; name: string } | null;
  level: UserLevel;
  parentId: string | null;
  /** Resolved chain above this user, for list columns. */
  chain: { master?: UserRef; distributor?: UserRef; retailer?: UserRef };
  group: { id: string; name: string } | null;
  status: RecordStatus;
  locked: boolean;
  disabledAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  createdBy: UserRef | null;
}

export interface UserOption extends UserRef {
  level: UserLevel;
  parentId: string | null;
}
